import useFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import { Client } from "@fartherfinance/frontend/api/Entity/requests/getAdvisorClients";
import { AdvisorRequestConfig } from "@fartherfinance/frontend/api/Types";

import { captureException } from "@src/multiCustodian/services/tracking";

interface UseTableDataWithAdvisorNamesReturn<T> {
  data: (T & { advisorName: { first: string; last: string } })[];
  isLoading: boolean;
  hasError: boolean;
}

export function useTableDataWithAdvisorNames<T extends Client>({
  tableData,
  auth,
  componentName,
}: {
  tableData: T[];
  auth: AdvisorRequestConfig | null;
  componentName: string;
}): UseTableDataWithAdvisorNamesReturn<T> {
  const advisors = useFartherEmployees("All", auth);

  const enrichedData = (tableData ?? []).map((data) => {
    const listOfAdvisors = advisors.data?.advisors;
    if (listOfAdvisors === undefined) {
      return {
        ...data,
        advisorName: {
          first: "-",
          last: "-",
        },
      };
    }

    const advisor = listOfAdvisors.find((a) => a.advisorId === data.advisorId);

    if (advisor === undefined) {
      const e = new Error(
        `[${componentName}.tsx]: Client has an advisorId that does not correlate to any existing Farther employee. This client was omitted from the list of clients.`
      );

      captureException(e, {
        extra: {
          clientId: "clientId" in data ? data.clientId : undefined,
          householdId: "householdId" in data ? data.householdId : undefined,
          advisorId: data?.advisorId,
          listOfAdvisorsLength: listOfAdvisors.length,
          listOfAdvisors: listOfAdvisors.map((a) => ({
            advisorId: a.advisorId,
            name: `${a.name.first} ${a.name.last}`,
          })),
        },
      });

      return {
        ...data,
        advisorName: {
          first: "-",
          last: "-",
        },
      };
    }

    return {
      ...data,
      advisorName: {
        first: advisor.name.first,
        last: advisor.name.last,
      },
    };
  });

  return {
    data: enrichedData,
    isLoading: advisors.isLoading,
    hasError: advisors.hasError,
  };
}
